import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ArrowUpRight, 
  Clock, 
  FileCheck, 
  TrendingUp, 
  AlertCircle, 
  Sparkles,
  Users,
  Target,
  Handshake,
  DollarSign,
  FolderKanban,
  CheckSquare,
  TicketCheck,
  Receipt,
  FileText,
  Phone,
  ArrowRight,
  ArrowLeft,
  Plus,
  BarChart3,
  Wallet,
  UserCircle,
  Building2,
  ShoppingCart,
  Package,
  Warehouse,
  UserCheck,
  MapPin,
  Briefcase,
  TruckIcon,
  Cigarette,
  Shield,
  Smartphone,
  Wrench,
  UtensilsCrossed,
  Coffee,
  Apple,
  Pill,
  Shirt,
  Scissors,
  Car,
  Boxes,
  CalendarClock,
  Timer,
  ThermometerSun,
  AlertTriangle,
  Star,
  Percent,
  CreditCard,
  Hash,
  Activity,
  Fuel,
  Gauge,
  Droplets
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { useSettings } from '../context/SettingsContext';
import SARSymbol from '../components/SARSymbol';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Business Type Configurations with unique dashboard layouts
const BUSINESS_DASHBOARDS = {
  smoke_shop: {
    name: 'Smoke Shop',
    icon: Cigarette,
    color: 'amber',
    heroColor: 'from-amber-500 to-orange-600',
    quickStats: [
      { key: 'age_verifications', label: '21+ Verified Today', icon: Shield, color: 'emerald' },
      { key: 'failed_verifications', label: 'Failed Checks', icon: AlertTriangle, color: 'red' },
      { key: 'tobacco_sales', label: 'Tobacco Sales', icon: Cigarette, color: 'amber' },
      { key: 'vape_sales', label: 'Vape/E-Cig Sales', icon: Activity, color: 'blue' },
    ],
    categories: ['Cigarettes', 'Vapes', 'CBD', 'Rolling Papers', 'Lighters', 'Accessories'],
    alerts: ['compliance', 'age_verify', 'tobacco_license'],
  },
  gas_station: {
    name: 'Gas Station',
    icon: Fuel,
    color: 'orange',
    heroColor: 'from-orange-500 to-amber-600',
    quickStats: [
      { key: 'gallons_sold', label: 'Gallons Sold Today', icon: Droplets, color: 'blue' },
      { key: 'fuel_revenue', label: 'Fuel Revenue', icon: DollarSign, color: 'emerald' },
      { key: 'store_sales', label: 'C-Store Sales', icon: ShoppingCart, color: 'purple' },
      { key: 'active_pumps', label: 'Active Pumps', icon: Gauge, color: 'amber' },
    ],
    categories: ['Fuel', 'Drinks', 'Snacks', 'Tobacco', 'Food', 'Lottery'],
    alerts: ['tank_low', 'pump_offline', 'drive_off'],
    posRoute: '/pos/gas-station',
  },
  mobile_electronics: {
    name: 'Mobile & Electronics',
    icon: Smartphone,
    color: 'blue',
    heroColor: 'from-blue-500 to-indigo-600',
    quickStats: [
      { key: 'repairs_pending', label: 'Repairs In Progress', icon: Wrench, color: 'orange' },
      { key: 'repairs_completed', label: 'Completed Today', icon: CheckSquare, color: 'emerald' },
      { key: 'trade_ins', label: 'Trade-Ins', icon: Smartphone, color: 'blue' },
      { key: 'accessories_sold', label: 'Accessories Sold', icon: Package, color: 'purple' },
    ],
    categories: ['Phones', 'Cases', 'Chargers', 'Screen Protectors', 'Earbuds', 'Repairs'],
    alerts: ['warranty_expiring', 'repair_overdue'],
  },
  restaurant_cafe: {
    name: 'Restaurant & Cafe',
    icon: UtensilsCrossed,
    color: 'rose',
    heroColor: 'from-rose-500 to-pink-600',
    quickStats: [
      { key: 'orders_today', label: 'Orders Today', icon: Receipt, color: 'emerald' },
      { key: 'avg_order_value', label: 'Avg Order Value', icon: DollarSign, color: 'blue' },
      { key: 'tables_served', label: 'Tables Served', icon: UtensilsCrossed, color: 'amber' },
      { key: 'peak_hour', label: 'Peak Hour', icon: Timer, color: 'purple' },
    ],
    categories: ['Drinks', 'Food', 'Desserts', 'Combos', 'Specials'],
    alerts: ['low_ingredients', 'long_wait_time'],
  },
  grocery_convenience: {
    name: 'Grocery & Convenience',
    icon: Apple,
    color: 'green',
    heroColor: 'from-green-500 to-emerald-600',
    quickStats: [
      { key: 'transactions_today', label: 'Transactions Today', icon: Receipt, color: 'blue' },
      { key: 'expiring_soon', label: 'Expiring in 7 Days', icon: CalendarClock, color: 'amber' },
      { key: 'fast_movers', label: 'Fast Moving Items', icon: TrendingUp, color: 'emerald' },
      { key: 'deliveries_due', label: 'Deliveries Due', icon: TruckIcon, color: 'purple' },
    ],
    categories: ['Snacks', 'Beverages', 'Dairy', 'Bread', 'Tobacco', 'Lottery'],
    alerts: ['expiring_products', 'delivery_arriving'],
  },
  pharmacy: {
    name: 'Pharmacy',
    icon: Pill,
    color: 'cyan',
    heroColor: 'from-cyan-500 to-teal-600',
    quickStats: [
      { key: 'prescriptions_filled', label: 'Rx Filled Today', icon: FileCheck, color: 'emerald' },
      { key: 'otc_sales', label: 'OTC Sales', icon: Package, color: 'blue' },
      { key: 'expiring_meds', label: 'Expiring Meds', icon: AlertCircle, color: 'red' },
      { key: 'insurance_pending', label: 'Insurance Pending', icon: CreditCard, color: 'amber' },
    ],
    categories: ['Prescription', 'OTC Meds', 'Vitamins', 'First Aid', 'Personal Care'],
    alerts: ['controlled_substance', 'insurance_rejected', 'expiring_meds'],
  },
  clothing_fashion: {
    name: 'Clothing & Fashion',
    icon: Shirt,
    color: 'pink',
    heroColor: 'from-pink-500 to-fuchsia-600',
    quickStats: [
      { key: 'items_sold', label: 'Items Sold Today', icon: Shirt, color: 'pink' },
      { key: 'returns', label: 'Returns Today', icon: Package, color: 'amber' },
      { key: 'top_category', label: 'Top Category', icon: Star, color: 'purple' },
      { key: 'promo_redemptions', label: 'Promo Used', icon: Percent, color: 'emerald' },
    ],
    categories: ["Men's", "Women's", 'Kids', 'Shoes', 'Accessories'],
    alerts: ['size_stockout', 'seasonal_clearance'],
  },
  salon_spa: {
    name: 'Salon & Spa',
    icon: Scissors,
    color: 'violet',
    heroColor: 'from-violet-500 to-purple-600',
    quickStats: [
      { key: 'appointments_today', label: 'Appointments Today', icon: CalendarClock, color: 'blue' },
      { key: 'completed', label: 'Completed', icon: CheckSquare, color: 'emerald' },
      { key: 'products_sold', label: 'Products Sold', icon: Package, color: 'pink' },
      { key: 'avg_service_time', label: 'Avg Service Time', icon: Timer, color: 'amber' },
    ],
    categories: ['Haircut', 'Color', 'Nails', 'Facial', 'Massage', 'Products'],
    alerts: ['appointment_reminder', 'product_low'],
  },
  auto_hardware: {
    name: 'Auto & Hardware',
    icon: Car,
    color: 'slate',
    heroColor: 'from-slate-600 to-zinc-700',
    quickStats: [
      { key: 'parts_sold', label: 'Parts Sold', icon: Package, color: 'blue' },
      { key: 'special_orders', label: 'Special Orders', icon: TruckIcon, color: 'amber' },
      { key: 'returns', label: 'Returns', icon: Package, color: 'red' },
      { key: 'top_brand', label: 'Top Brand', icon: Star, color: 'emerald' },
    ],
    categories: ['Motor Oil', 'Batteries', 'Tools', 'Parts', 'Tires', 'Cleaning'],
    alerts: ['special_order_ready', 'core_return_due'],
  },
  wholesale_distribution: {
    name: 'Wholesale',
    icon: Boxes,
    color: 'indigo',
    heroColor: 'from-indigo-500 to-blue-600',
    quickStats: [
      { key: 'orders_today', label: 'Orders Today', icon: ShoppingCart, color: 'blue' },
      { key: 'cases_shipped', label: 'Cases Shipped', icon: Boxes, color: 'emerald' },
      { key: 'pending_shipments', label: 'Pending Shipments', icon: TruckIcon, color: 'amber' },
      { key: 'new_accounts', label: 'New Accounts', icon: Users, color: 'purple' },
    ],
    categories: ['Bulk Items', 'Case Deals', 'New Stock', 'Clearance'],
    alerts: ['bulk_order_ready', 'account_credit_limit'],
  },
  retail_store: {
    name: 'General Retail',
    icon: ShoppingCart,
    color: 'emerald',
    heroColor: 'from-emerald-500 to-teal-600',
    quickStats: [
      { key: 'sales_today', label: 'Sales Today', icon: DollarSign, color: 'emerald' },
      { key: 'transactions', label: 'Transactions', icon: Receipt, color: 'blue' },
      { key: 'avg_basket', label: 'Avg Basket Size', icon: ShoppingCart, color: 'purple' },
      { key: 'returns', label: 'Returns', icon: Package, color: 'amber' },
    ],
    categories: ['Popular', 'New Arrivals', 'Sale', 'Essentials'],
    alerts: ['low_stock', 'price_change'],
  },
};

// Currency display helper component
const CurrencyValue = ({ value, symbol, isSAR, suffix = '', className = '' }) => {
  if (isSAR) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`}>
        <SARSymbol size={20} />
        <span>{value}{suffix}</span>
      </span>
    );
  }
  return <span className={className}>{symbol} {value}{suffix}</span>;
};

const StatCard = ({ title, value, subtitle, icon: Icon, color, bgColor, link, isRTL }) => (
  <Link to={link || '#'}>
    <Card className="bg-card border-border relative overflow-hidden group hover:border-primary/30 hover:shadow-lg transition-all duration-300 cursor-pointer">
      <div className={`absolute inset-0 ${bgColor} opacity-50 group-hover:opacity-70 transition-opacity`}></div>
      <CardContent className="p-4 relative z-10">
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={isRTL ? 'text-right' : ''}>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className={`p-2.5 rounded-xl ${bgColor} group-hover:scale-110 transition-transform`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol, isSAR } = useSettings();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessType, setBusinessType] = useState('retail_store');
  const [categoryStats, setCategoryStats] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [reportStats, activity, accessData] = await Promise.all([
        axios.get(`${API_URL}/api/reports/dashboard`),
        axios.get(`${API_URL}/api/reports/recent-activity`),
        axios.get(`${API_URL}/api/subscriptions/my-access`).catch(() => ({ data: {} }))
      ]);
      setStats(reportStats.data);
      setRecentActivity(activity.data);
      
      // Check if user is admin from localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const email = userData?.email || '';
          setUserEmail(email);
          setIsAdmin(userData?.role === 'admin');
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      
      // Set business type from user's subscription
      if (accessData.data?.business_type) {
        setBusinessType(accessData.data.business_type);
      }
      
      // Generate mock category-specific stats (in production, fetch from API)
      generateCategoryStats(accessData.data?.business_type || 'retail_store');
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle category switch (Admin only)
  const handleCategorySwitch = (newType) => {
    setBusinessType(newType);
    generateCategoryStats(newType);
  };

  // Generate category-specific stats (mock data - replace with real API)
  const generateCategoryStats = (type) => {
    const mockStats = {
      smoke_shop: {
        age_verifications: 47,
        failed_verifications: 3,
        tobacco_sales: 1250,
        vape_sales: 890,
        top_products: [
          { name: 'Marlboro Red', sales: 45, stock: 120 },
          { name: 'Juul Pods', sales: 38, stock: 85 },
          { name: 'RAW Papers', sales: 32, stock: 200 },
          { name: 'Bic Lighters', sales: 28, stock: 150 },
        ],
        compliance_score: 98,
      },
      gas_station: {
        gallons_sold: 4250,
        fuel_revenue: 14875,
        store_sales: 2340,
        active_pumps: 6,
        top_products: [
          { name: 'Regular Unleaded', sales: 2100, stock: 8500 },
          { name: 'Premium Unleaded', sales: 980, stock: 4800 },
          { name: 'Coffee Large', sales: 145, stock: 999 },
          { name: 'Red Bull', sales: 89, stock: 80 },
        ],
        tank_levels: {
          regular: 71,
          plus: 53,
          premium: 60,
          diesel: 18,
        },
        pump_status: { online: 7, offline: 1 },
      },
      mobile_electronics: {
        repairs_pending: 12,
        repairs_completed: 8,
        trade_ins: 5,
        accessories_sold: 34,
        top_products: [
          { name: 'iPhone Cases', sales: 23, stock: 85 },
          { name: 'Screen Protectors', sales: 19, stock: 120 },
          { name: 'USB-C Cables', sales: 15, stock: 200 },
          { name: 'AirPods', sales: 8, stock: 25 },
        ],
        warranty_alerts: 3,
      },
      restaurant_cafe: {
        orders_today: 156,
        avg_order_value: 18.50,
        tables_served: 89,
        peak_hour: '12:00 PM',
        top_products: [
          { name: 'Coffee Latte', sales: 78, stock: 999 },
          { name: 'Club Sandwich', sales: 45, stock: 999 },
          { name: 'Caesar Salad', sales: 32, stock: 999 },
          { name: 'Cheesecake', sales: 28, stock: 12 },
        ],
        avg_wait_time: '8 min',
      },
      grocery_convenience: {
        transactions_today: 234,
        expiring_soon: 18,
        fast_movers: 12,
        deliveries_due: 3,
        top_products: [
          { name: 'Milk 1 Gallon', sales: 45, stock: 32 },
          { name: 'Bread Loaf', sales: 38, stock: 24 },
          { name: 'Coca Cola 20oz', sales: 67, stock: 120 },
          { name: 'Doritos', sales: 34, stock: 85 },
        ],
        lottery_sales: 450,
      },
      pharmacy: {
        prescriptions_filled: 67,
        otc_sales: 1890,
        expiring_meds: 8,
        insurance_pending: 12,
        top_products: [
          { name: 'Tylenol', sales: 34, stock: 120 },
          { name: 'Vitamin D', sales: 28, stock: 85 },
          { name: 'Band-Aids', sales: 22, stock: 200 },
          { name: 'Advil', sales: 19, stock: 95 },
        ],
        controlled_alerts: 2,
      },
      clothing_fashion: {
        items_sold: 89,
        returns: 7,
        top_category: "Women's",
        promo_redemptions: 23,
        top_products: [
          { name: 'Summer Dress', sales: 18, stock: 12 },
          { name: 'Denim Jeans', sales: 15, stock: 28 },
          { name: 'T-Shirt Basic', sales: 34, stock: 85 },
          { name: 'Sneakers', sales: 12, stock: 18 },
        ],
        size_alerts: ['M - Low', 'L - Out'],
      },
      salon_spa: {
        appointments_today: 24,
        completed: 18,
        products_sold: 12,
        avg_service_time: '45 min',
        top_products: [
          { name: 'Haircut Men', sales: 12, stock: 999 },
          { name: 'Haircut Women', sales: 8, stock: 999 },
          { name: 'Hair Color', sales: 6, stock: 999 },
          { name: 'Shampoo Pro', sales: 9, stock: 24 },
        ],
        upcoming_appointments: 6,
      },
      auto_hardware: {
        parts_sold: 67,
        special_orders: 8,
        returns: 4,
        top_brand: 'Bosch',
        top_products: [
          { name: 'Motor Oil 5W-30', sales: 28, stock: 85 },
          { name: 'Brake Pads', sales: 12, stock: 32 },
          { name: 'Air Filters', sales: 18, stock: 45 },
          { name: 'Wiper Blades', sales: 15, stock: 60 },
        ],
        core_returns_due: 3,
      },
      wholesale_distribution: {
        orders_today: 45,
        cases_shipped: 234,
        pending_shipments: 12,
        new_accounts: 3,
        top_products: [
          { name: 'Snacks Case', sales: 45, stock: 120 },
          { name: 'Beverage Case', sales: 38, stock: 85 },
          { name: 'Candy Box', sales: 28, stock: 200 },
          { name: 'Chips Case', sales: 22, stock: 150 },
        ],
        account_alerts: 2,
      },
      retail_store: {
        sales_today: 2450,
        transactions: 89,
        avg_basket: 27.50,
        returns: 5,
        top_products: [
          { name: 'Popular Item 1', sales: 34, stock: 85 },
          { name: 'Popular Item 2', sales: 28, stock: 120 },
          { name: 'Popular Item 3', sales: 22, stock: 45 },
          { name: 'Popular Item 4', sales: 18, stock: 60 },
        ],
        promo_active: 3,
      },
    };
    setCategoryStats(mockStats[type] || mockStats.retail_store);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const config = BUSINESS_DASHBOARDS[businessType] || BUSINESS_DASHBOARDS.retail_store;
  const BusinessIcon = config.icon;

  const activityIcons = {
    deal: Handshake,
    client: Users,
    ticket: TicketCheck,
    lead: Target,
    project: FolderKanban,
    task: CheckSquare,
    invoice: Receipt
  };

  const activityColors = {
    deal: 'bg-purple-100 text-purple-600',
    client: 'bg-blue-100 text-blue-600',
    ticket: 'bg-amber-100 text-amber-600',
    lead: 'bg-orange-100 text-orange-600',
    project: 'bg-indigo-100 text-indigo-600',
    task: 'bg-teal-100 text-teal-600',
    invoice: 'bg-emerald-100 text-emerald-600'
  };

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  // Helper to get stat value with formatting
  const getStatValue = (key) => {
    const val = categoryStats[key];
    if (typeof val === 'number') {
      if (key.includes('sales') || key.includes('value') || key === 'avg_basket' || key === 'avg_order_value') {
        return `${currencySymbol}${val.toLocaleString()}`;
      }
      return val.toLocaleString();
    }
    return val || '0';
  };

  return (
    <div className="space-y-6">
      {/* Dynamic Header with Business Type */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : ''}>
          <div className="flex items-center gap-3 mb-1">
            <div className={`p-2 rounded-xl bg-gradient-to-br ${config.heroColor}`}>
              <BusinessIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">{config.name} Dashboard</h1>
                {/* Admin Category Selector */}
                {isAdmin && (
                  <Select value={businessType} onValueChange={handleCategorySwitch}>
                    <SelectTrigger className="w-56 h-9 border-dashed" data-testid="dashboard-category-selector">
                      <SelectValue placeholder="Select Category">
                        <span className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full bg-${config.color}-500`}></span>
                          <span className="font-medium text-sm">{config.name}</span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(BUSINESS_DASHBOARDS).map(([id, cat]) => {
                        const CatIcon = cat.icon;
                        return (
                          <SelectItem key={id} value={id}>
                            <span className="flex items-center gap-2">
                              <CatIcon className={`h-4 w-4 text-${cat.color}-500`} />
                              <span>{cat.name}</span>
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Real-time insights for your business
                {isAdmin && <span className="ml-2 text-xs text-amber-600">(Admin Preview Mode)</span>}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={businessType === 'gas_station' ? '/pos/gas-station' : `/pos?industry=${businessType}`}>
            <Button className={`gap-2 bg-gradient-to-r ${config.heroColor} hover:opacity-90`}>
              <ShoppingCart className="h-4 w-4" /> Open POS
            </Button>
          </Link>
          <Link to="/reports">
            <Button variant="outline" size="sm" className="gap-2">
              <BarChart3 className="h-4 w-4" /> Reports
            </Button>
          </Link>
        </div>
      </div>

      {/* Category-Specific Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {config.quickStats.map((stat, idx) => {
          const StatIcon = stat.icon;
          return (
            <Card key={idx} className={`bg-gradient-to-br from-${stat.color}-500 to-${stat.color}-600 text-white border-0 shadow-lg`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-${stat.color}-100 text-sm font-medium`}>{stat.label}</span>
                  <StatIcon className={`h-5 w-5 text-${stat.color}-200`} />
                </div>
                <div className="text-3xl font-bold">{getStatValue(stat.key)}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Financial Row */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Total Revenue */}
        <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-emerald-100 text-sm font-medium">Total Revenue</span>
              <Receipt className="h-5 w-5 text-emerald-200" />
            </div>
            <div className="text-3xl font-bold mb-1">
              <CurrencyValue 
                value={((stats?.finance?.paid || 0) / 1000).toFixed(1)} 
                symbol={currencySymbol} 
                isSAR={isSAR}
                suffix="K"
              />
            </div>
            <div className="text-emerald-200 text-sm">
              {stats?.finance?.pending ? `${currencySymbol}${(stats.finance.pending / 1000).toFixed(1)}K pending` : 'All settled'}
            </div>
            <Link to="/invoices">
              <Button variant="secondary" size="sm" className="mt-4 bg-white/20 hover:bg-white/30 text-white border-0">
                View Transactions <ArrowIcon className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Inventory Value */}
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-blue-100 text-sm font-medium">Inventory Value</span>
              <Package className="h-5 w-5 text-blue-200" />
            </div>
            <div className="text-3xl font-bold mb-1">
              <CurrencyValue 
                value={((stats?.inventory?.total_value || 0) / 1000).toFixed(1)} 
                symbol={currencySymbol} 
                isSAR={isSAR}
                suffix="K"
              />
            </div>
            <div className="text-blue-200 text-sm">
              {stats?.inventory?.products?.total || 0} products in stock
            </div>
            <Link to="/products">
              <Button variant="secondary" size="sm" className="mt-4 bg-white/20 hover:bg-white/30 text-white border-0">
                View Products <ArrowIcon className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Expenses */}
        <Card className="bg-gradient-to-br from-rose-500 to-pink-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-rose-100 text-sm font-medium">Expenses</span>
              <Wallet className="h-5 w-5 text-rose-200" />
            </div>
            <div className="text-3xl font-bold mb-1">
              <CurrencyValue 
                value={((stats?.finance?.total_expenses || 0) / 1000).toFixed(1)} 
                symbol={currencySymbol} 
                isSAR={isSAR}
                suffix="K"
              />
            </div>
            <div className="text-rose-200 text-sm">
              Net: <CurrencyValue 
                value={((stats?.finance?.net_revenue || 0) / 1000).toFixed(1)} 
                symbol={currencySymbol} 
                isSAR={isSAR}
                suffix="K"
              />
            </div>
            <Link to="/expenses">
              <Button variant="secondary" size="sm" className="mt-4 bg-white/20 hover:bg-white/30 text-white border-0">
                View Expenses <ArrowIcon className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Top Products & Quick Actions Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Products for Category */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" /> Top Products
            </CardTitle>
            <Link to="/products">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(categoryStats.top_products || []).map((product, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.heroColor} flex items-center justify-center text-white font-bold text-sm`}>
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.sales} sold today</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={product.stock < 30 ? "destructive" : product.stock < 100 ? "secondary" : "outline"}>
                      {product.stock === 999 ? '∞' : product.stock} in stock
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" /> Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to={`/pos?industry=${businessType}`}>
              <Button className={`w-full justify-start gap-2 h-10 bg-gradient-to-r ${config.heroColor} hover:opacity-90`}>
                <ShoppingCart className="h-4 w-4" /> Open POS Terminal
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="w-full justify-start gap-2 h-10">
                <Plus className="h-4 w-4" /> Add New Product
              </Button>
            </Link>
            <Link to="/purchase-orders">
              <Button variant="outline" className="w-full justify-start gap-2 h-10">
                <Plus className="h-4 w-4" /> New Purchase Order
              </Button>
            </Link>
            <Link to="/suppliers">
              <Button variant="outline" className="w-full justify-start gap-2 h-10">
                <Plus className="h-4 w-4" /> Add Supplier
              </Button>
            </Link>
            <Link to="/expenses">
              <Button variant="outline" className="w-full justify-start gap-2 h-10">
                <Plus className="h-4 w-4" /> Record Expense
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Operations Summary */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-violet-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-purple-100 text-sm">Purchase Orders</span>
              <ShoppingCart className="h-4 w-4 text-purple-200" />
            </div>
            <div className="text-2xl font-bold">{stats?.procurement?.purchase_orders?.total || 0}</div>
            <div className="text-purple-200 text-xs">{stats?.procurement?.purchase_orders?.pending || 0} pending</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-amber-100 text-sm">PO Value</span>
              <DollarSign className="h-4 w-4 text-amber-200" />
            </div>
            <div className="text-2xl font-bold">
              <CurrencyValue 
                value={((stats?.procurement?.purchase_orders?.total_value || 0) / 1000).toFixed(0)} 
                symbol={currencySymbol} 
                isSAR={isSAR}
                suffix="K"
              />
            </div>
            <div className="text-amber-200 text-xs">{stats?.procurement?.purchase_orders?.received || 0} received</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-500 to-teal-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-cyan-100 text-sm">Staff</span>
              <UserCheck className="h-4 w-4 text-cyan-200" />
            </div>
            <div className="text-2xl font-bold">{stats?.hr?.employees?.total || 0}</div>
            <div className="text-cyan-200 text-xs">{stats?.hr?.attendance_today || 0} present today</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-rose-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-red-100 text-sm">Low Stock Alert</span>
              <AlertCircle className="h-4 w-4 text-red-200" />
            </div>
            <div className="text-2xl font-bold">{stats?.inventory?.products?.low_stock || 0}</div>
            <div className="text-red-200 text-xs">items need reorder</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity & Category-Specific Info */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" /> Recent Activity
            </CardTitle>
            <Link to="/reports">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.slice(0, 5).map((activity, idx) => {
                  const Icon = activityIcons[activity.type] || FileText;
                  const colorClass = activityColors[activity.type] || 'bg-gray-100 text-gray-600';
                  return (
                    <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{activity.title}</p>
                        <p className="text-xs text-muted-foreground">{activity.description}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {activity.time ? format(new Date(activity.time), 'MMM d, h:mm a') : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category-Specific Alerts/Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" /> {config.name} Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Dynamic alerts based on business type */}
            {businessType === 'smoke_shop' && (
              <>
                <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm font-medium">Compliance Score</span>
                  </div>
                  <Badge className="bg-emerald-600">{categoryStats.compliance_score || 100}%</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium">Failed 21+ Checks</span>
                  </div>
                  <Badge variant="secondary">{categoryStats.failed_verifications || 0}</Badge>
                </div>
              </>
            )}
            {businessType === 'mobile_electronics' && (
              <>
                <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-orange-600" />
                    <span className="text-sm font-medium">Repairs Pending</span>
                  </div>
                  <Badge variant="secondary">{categoryStats.repairs_pending || 0}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium">Warranty Expiring</span>
                  </div>
                  <Badge variant="secondary">{categoryStats.warranty_alerts || 0}</Badge>
                </div>
              </>
            )}
            {businessType === 'restaurant_cafe' && (
              <>
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Timer className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium">Avg Wait Time</span>
                  </div>
                  <Badge variant="secondary">{categoryStats.avg_wait_time || 'N/A'}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium">Peak Hour</span>
                  </div>
                  <Badge variant="secondary">{categoryStats.peak_hour || 'N/A'}</Badge>
                </div>
              </>
            )}
            {businessType === 'grocery_convenience' && (
              <>
                <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium">Expiring Soon</span>
                  </div>
                  <Badge variant="destructive">{categoryStats.expiring_soon || 0}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm font-medium">Lottery Sales</span>
                  </div>
                  <Badge className="bg-emerald-600">${categoryStats.lottery_sales || 0}</Badge>
                </div>
              </>
            )}
            {businessType === 'pharmacy' && (
              <>
                <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-medium">Expiring Meds</span>
                  </div>
                  <Badge variant="destructive">{categoryStats.expiring_meds || 0}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium">Insurance Pending</span>
                  </div>
                  <Badge variant="secondary">{categoryStats.insurance_pending || 0}</Badge>
                </div>
              </>
            )}
            {businessType === 'gas_station' && (
              <>
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Droplets className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium">Gallons Sold Today</span>
                  </div>
                  <Badge className="bg-blue-600">{categoryStats.gallons_sold?.toLocaleString() || 0}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm font-medium">Pumps Online</span>
                  </div>
                  <Badge className="bg-emerald-600">{categoryStats.pump_status?.online || 0} / {(categoryStats.pump_status?.online || 0) + (categoryStats.pump_status?.offline || 0)}</Badge>
                </div>
                {categoryStats.tank_levels?.diesel < 20 && (
                  <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      <span className="text-sm font-medium">Low Tank: Diesel</span>
                    </div>
                    <Badge variant="destructive">{categoryStats.tank_levels?.diesel}%</Badge>
                  </div>
                )}
              </>
            )}
            {(businessType === 'retail_store' || businessType === 'clothing_fashion' || businessType === 'salon_spa' || businessType === 'auto_hardware' || businessType === 'wholesale_distribution') && (
              <>
                <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium">Low Stock Items</span>
                  </div>
                  <Badge variant="secondary">{stats?.inventory?.products?.low_stock || 0}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <TruckIcon className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-medium">Pending Orders</span>
                  </div>
                  <Badge variant="secondary">{stats?.procurement?.purchase_orders?.pending || 0}</Badge>
                </div>
              </>
            )}
            
            {/* Common supplier info */}
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Active Suppliers</span>
                <span className="font-medium">{stats?.procurement?.suppliers?.active || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard; 
